import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ShootingSchedule {
  id: number;
  date: string;
  time: string;
  customerName: string;
  shootingType: string;
  photographer: string;
  assistant: string;
  location: string;
  concept: string;
  status: string;
  memo: string;
}

export default function VendorStudioSchedulePage() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date(2024, 0, 1));
  const [selectedDate, setSelectedDate] = useState('2024-01-25');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ShootingSchedule | null>(null);

  const menuItems = [
    { id: 'dashboard', icon: 'ri-dashboard-line', label: '대시보드', path: '/vendor-portal' },
    { id: 'orders', icon: 'ri-file-list-3-line', label: '발주서 관리', path: '/vendor-portal/orders' },
    { id: 'schedule', icon: 'ri-calendar-line', label: '촬영 일정 관리', path: '/vendor-portal/studio-schedule' },
    { id: 'ordersheet', icon: 'ri-file-text-line', label: '촬영 오더시트', path: '/vendor-portal/studio-customers' },
    { id: 'staff', icon: 'ri-team-line', label: '작가·스태프 배정', path: '/vendor-portal/studio-staff' },
    { id: 'photos', icon: 'ri-folder-image-line', label: '촬영 사진 관리', path: '/vendor-portal/studio-photos' },
    { id: 'collaboration', icon: 'ri-user-star-line', label: '플래너 협업 관리', path: '/vendor-portal/studio-requests' },
    { id: 'invoice', icon: 'ri-file-text-line', label: '인보이스 관리', path: '/vendor-portal/invoice' },
    { id: 'tax', icon: 'ri-file-list-line', label: '세금계산서 관리', path: '/vendor-portal/tax' },
    { id: 'products', icon: 'ri-shopping-bag-line', label: '상품 관리', path: '/vendor-portal/products' }
  ];

  const [schedules, setSchedules] = useState<ShootingSchedule[]>([
    {
      id: 1,
      date: '2024-01-25',
      time: '10:00',
      customerName: '김지은',
      shootingType: '리허설',
      photographer: '박작가',
      assistant: '김보조',
      location: '스튜디오 A',
      concept: '내추럴',
      status: '확정',
      memo: '가족사진 포함'
    },
    {
      id: 2,
      date: '2024-01-25',
      time: '14:00',
      customerName: '이민수',
      shootingType: '본식스냅',
      photographer: '최작가',
      assistant: '이보조',
      location: '그랜드웨딩홀',
      concept: '클래식',
      status: '확정',
      memo: '영상 동시 촬영'
    },
    {
      id: 3,
      date: '2024-01-26',
      time: '11:00',
      customerName: '박영희',
      shootingType: '본식영상',
      photographer: '정작가',
      assistant: '박보조',
      location: '로맨틱웨딩홀',
      concept: '모던',
      status: '대기',
      memo: '드론 촬영 요청'
    },
    {
      id: 4,
      date: '2024-01-28',
      time: '09:00',
      customerName: '최수진',
      shootingType: '리허설',
      photographer: '박작가',
      assistant: '김보조',
      location: '스튜디오 B',
      concept: '로맨틱',
      status: '확정',
      memo: '야외 촬영 포함'
    }
  ]);

  const shootingTypes = ['리허설', '본식스냅', '본식영상', '스냅+영상', '야외촬영'];
  const photographers = ['박작가', '최작가', '정작가', '김작가', '이작가'];
  const assistants = ['김보조', '이보조', '박보조', '최보조'];
  const concepts = ['내추럴', '모던', '클래식', '로맨틱', '빈티지'];
  const statusTypes = ['대기', '확정', '완료', '취소'];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (year: number, month: number, day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getSchedulesForDate = (dateString: string) => {
    return schedules.filter(schedule => schedule.date === dateString);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    const weekHeader = weekDays.map(day => (
      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">
        {day}
      </div>
    ));

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-28 border border-gray-200"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day);
      const daySchedules = getSchedulesForDate(dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];
      const isSelected = dateString === selectedDate;

      days.push(
        <div
          key={day}
          className={`p-2 h-28 border border-gray-200 cursor-pointer hover:bg-gray-50 ${
            isToday ? 'bg-blue-50' : ''
          } ${isSelected ? 'bg-blue-100' : ''}`}
          onClick={() => setSelectedDate(dateString)}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {daySchedules.slice(0, 2).map((schedule) => (
              <div
                key={schedule.id}
                className={`text-xs p-1 rounded truncate cursor-pointer ${
                  schedule.shootingType === '리허설' ? 'bg-purple-100 text-purple-800' :
                  schedule.shootingType === '본식스냅' ? 'bg-blue-100 text-blue-800' :
                  schedule.shootingType === '본식영상' ? 'bg-green-100 text-green-800' :
                  'bg-orange-100 text-orange-800'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditSchedule(schedule);
                }}
              >
                {schedule.time} {schedule.customerName}
              </div>
            ))}
            {daySchedules.length > 2 && (
              <div className="text-xs text-gray-500">
                +{daySchedules.length - 2}개 더
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">
          {weekHeader}
          {days}
        </div>
      </div>
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const newSchedule: ShootingSchedule = {
      id: selectedSchedule ? selectedSchedule.id : schedules.length + 1,
      date: formData.get('date') as string,
      time: formData.get('time') as string,
      customerName: formData.get('customerName') as string,
      shootingType: formData.get('shootingType') as string,
      photographer: formData.get('photographer') as string,
      assistant: formData.get('assistant') as string,
      location: formData.get('location') as string,
      concept: formData.get('concept') as string,
      status: formData.get('status') as string,
      memo: formData.get('memo') as string
    };

    if (selectedSchedule) {
      setSchedules(schedules.map(s => s.id === selectedSchedule.id ? newSchedule : s));
    } else {
      setSchedules([...schedules, newSchedule]);
    }

    setShowAddModal(false);
    setSelectedSchedule(null);
  };

  const handleEditSchedule = (schedule: ShootingSchedule) => {
    setSelectedSchedule(schedule);
    setShowAddModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 사이드바 */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* 로고 영역 */}
        <div className="p-6 border-b border-gray-200">
          {isSidebarOpen ? (
            <div>
              <h1 className="text-xl font-bold text-gray-800">메이스튜디오</h1>
              <p className="text-sm text-gray-600 mt-1">업체 포털</p>
            </div>
          ) : (
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
          )}
        </div>

        {/* 메뉴 */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center px-6 py-3 text-left hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer ${
                item.path === '/vendor-portal/studio-schedule' ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-700'
              }`}
            >
              <i className={`${item.icon} text-lg ${isSidebarOpen ? 'mr-3' : ''}`}></i>
              {isSidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* 하단 사용자 정보 */}
        <div className="p-4 border-t border-gray-200">
          {isSidebarOpen ? (
            <div>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-user-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">김대표</p>
                  <p className="text-xs text-gray-600">메이스튜디오</p>
                </div>
              </div>
              <button className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap cursor-pointer">
                <i className="ri-logout-box-line mr-2"></i>로그아웃
              </button>
            </div>
          ) : (
            <button className="w-full p-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              <i className="ri-logout-box-line text-lg"></i>
            </button>
          )}
        </div>
      </aside>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 flex flex-col">
        {/* 헤더 */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="mr-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
              <h2 className="text-xl font-semibold text-gray-900">촬영 일정 관리</h2>
            </div>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* 컨텐츠 영역 */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="text-sm text-gray-600 mt-1">스튜디오 촬영 스케줄을 관리하세요</p>
              </div>
              <button
                onClick={() => {
                  setSelectedSchedule(null);
                  setShowAddModal(true);
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line mr-2"></i>
                촬영 일정 등록
              </button>
            </div>

            {/* 캘린더 헤더 */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-gray-600"></i>
                </button>
                <h2 className="text-xl font-semibold text-gray-800">
                  {currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월
                </h2>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-gray-600"></i>
                </button>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-100 rounded"></div>
                  <span>리허설</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-100 rounded"></div>
                  <span>본식스냅</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-100 rounded"></div>
                  <span>본식영상</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-100 rounded"></div>
                  <span>기타</span>
                </div>
              </div>
            </div>

            {/* 캘린더 */}
            {renderCalendar()}

            {/* 선택된 날짜의 촬영 일정 */}
            {selectedDate && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  {selectedDate} 촬영 일정
                </h3>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  {getSchedulesForDate(selectedDate).length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {getSchedulesForDate(selectedDate).map((schedule) => (
                        <div key={schedule.id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-medium text-gray-900">
                                  {schedule.time}
                                </span>
                                <span className="font-medium text-gray-900">
                                  {schedule.customerName}
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  schedule.shootingType === '리허설' ? 'bg-purple-100 text-purple-800' :
                                  schedule.shootingType === '본식스냅' ? 'bg-blue-100 text-blue-800' :
                                  schedule.shootingType === '본식영상' ? 'bg-green-100 text-green-800' :
                                  'bg-orange-100 text-orange-800'
                                }`}>
                                  {schedule.shootingType}
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  schedule.status === '확정' ? 'bg-green-100 text-green-800' :
                                  schedule.status === '대기' ? 'bg-yellow-100 text-yellow-800' :
                                  schedule.status === '완료' ? 'bg-gray-100 text-gray-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {schedule.status}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <div>작가: {schedule.photographer}</div>
                                <div>보조: {schedule.assistant}</div>
                                <div>장소: {schedule.location}</div>
                                <div>컨셉: {schedule.concept}</div>
                              </div>
                              {schedule.memo && (
                                <div className="text-sm text-gray-600 mt-2">
                                  메모: {schedule.memo}
                                </div>
                              )}
                            </div>
                            <button 
                              onClick={() => handleEditSchedule(schedule)}
                              className="text-blue-600 hover:text-blue-800 w-6 h-6 flex items-center justify-center cursor-pointer"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      선택된 날짜에 촬영 일정이 없습니다.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* 촬영 일정 등록/수정 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {selectedSchedule ? '촬영 일정 수정' : '촬영 일정 등록'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedSchedule(null);
                }}
                className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">날짜</label>
                  <input 
                    type="date" 
                    name="date"
                    defaultValue={selectedSchedule?.date || selectedDate}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">시간</label>
                  <input 
                    type="time" 
                    name="time"
                    defaultValue={selectedSchedule?.time || ''}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                <input 
                  type="text" 
                  name="customerName"
                  defaultValue={selectedSchedule?.customerName || ''}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2" 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">촬영 유형</label>
                  <select 
                    name="shootingType"
                    defaultValue={selectedSchedule?.shootingType || ''}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                  >
                    <option value="">선택하세요</option>
                    {shootingTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">컨셉</label>
                  <select 
                    name="concept"
                    defaultValue={selectedSchedule?.concept || ''}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                  >
                    <option value="">선택하세요</option>
                    {concepts.map(concept => (
                      <option key={concept} value={concept}>{concept}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">작가</label>
                  <select 
                    name="photographer"
                    defaultValue={selectedSchedule?.photographer || ''}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                  >
                    <option value="">선택하세요</option>
                    {photographers.map(photographer => (
                      <option key={photographer} value={photographer}>{photographer}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">보조</label>
                  <select 
                    name="assistant"
                    defaultValue={selectedSchedule?.assistant || ''}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                  >
                    <option value="">선택하세요</option>
                    {assistants.map(assistant => (
                      <option key={assistant} value={assistant}>{assistant}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">촬영 장소</label>
                <input 
                  type="text" 
                  name="location"
                  defaultValue={selectedSchedule?.location || ''}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="예: 스튜디오 A, 그랜드웨딩홀" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상태</label>
                <select 
                  name="status"
                  defaultValue={selectedSchedule?.status || '대기'}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer"
                >
                  {statusTypes.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                <textarea 
                  name="memo"
                  defaultValue={selectedSchedule?.memo || ''}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                  placeholder="특이사항을 입력하세요"
                ></textarea>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedSchedule(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap cursor-pointer"
                >
                  {selectedSchedule ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}